import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { chatTemplateService } from "@/features/other-setup/services/chatTemplateService";
import {
  getPresignedUrl,
  uploadToS3Direct,
  confirmAttachmentInDB,
  deleteAttachmentFromAll,
  getAttachmentViewingUrl,
} from "@/shared/services/uploadService";
import api from "@/shared/services/apiClient";
import type {
  ChatTemplate,
  TemplateAttachment,
  UpdateTemplatePayload,
} from "@/features/other-setup/types/chatTemplate";

// ─── Template Form Modal ──────────────────────────────────────────────────────

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.txt";
const isImage = (f: { file_type?: string; display_name?: string }) =>
  f.file_type?.startsWith("image/") ||
  /\.(jpg|jpeg|png|gif|webp)$/i.test(f.display_name ?? "");

interface TemplateFormProps {
  template: ChatTemplate | null;
  existingCategories: string[];
  onSave: (data: UpdateTemplatePayload) => Promise<ChatTemplate>;
  onClose: () => void;
}

const TemplateFormModal = ({
  template,
  existingCategories,
  onSave,
  onClose,
}: TemplateFormProps) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(template?.title ?? "");
  const [body, setBody] = useState(template?.body ?? "");
  const [category, setCategory] = useState(template?.category ?? "");
  const [isActive, setIsActive] = useState(template?.is_active ?? true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ title?: string; body?: string }>({});
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<TemplateAttachment[]>(
    Array.isArray(template?.attachments) ? template.attachments : [],
  );
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Refresh attachments when editing an existing template
  useEffect(() => {
    if (!template?.row_id) return;
    api.get(`/attachments/chat_template/${template.row_id}`).then((res) => {
      const list = res.data?.attachments;
      setExistingAttachments(Array.isArray(list) ? list : []);
    }).catch(() => {});
  }, [template?.row_id]);

  const validate = () => {
    const e: { title?: string; body?: string } = {};
    if (!title.trim()) e.title = t("chat_templates.form.field_title_required");
    if (!body.trim()) e.body = t("chat_templates.form.field_body_required");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length) setPendingFiles((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const removePending = (index: number) =>
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));

  const removeExisting = async (attachmentId: string) => {
    await deleteAttachmentFromAll(attachmentId);
    setExistingAttachments((prev) => prev.filter((a) => a.attachment_id !== attachmentId));
  };

  const openAttachment = async (attachmentId: string) => {
    const url = await getAttachmentViewingUrl(attachmentId);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const uploadPendingFiles = async (rowId: number) => {
    for (const file of pendingFiles) {
      const key = file.name + file.size;
      try {
        setUploadProgress((p) => ({ ...p, [key]: 0 }));
        const { uploadUrl, s3Key, bucketName } = await getPresignedUrl(
          file.name, file.type, "chat_template", rowId,
        );
        await uploadToS3Direct(uploadUrl, file, file.type, (pct) =>
          setUploadProgress((p) => ({ ...p, [key]: pct })),
        );
        await confirmAttachmentInDB({
          relation_type: "chat_template",
          relation_id: String(rowId),
          s3_key: s3Key,
          s3_bucket: bucketName,
          display_name: file.name,
          file_type: file.type,
          file_size: file.size,
        });
      } finally {
        setUploadProgress((p) => { const n = { ...p }; delete n[key]; return n; });
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const saved = await onSave({
        title: title.trim(),
        body: body.trim(),
        category: category.trim() || null,
        is_active: isActive,
      });
      if (pendingFiles.length) await uploadPendingFiles(saved.row_id);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">
            {template ? t("chat_templates.form.edit_title") : t("chat_templates.form.new_title")}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("chat_templates.form.field_title")} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
              placeholder={t("chat_templates.form.field_title_placeholder")}
              className={`w-full px-3 py-2 text-sm border rounded-lg outline-none transition-colors
                ${errors.title ? "border-red-400 focus:border-red-500" : "border-gray-300 focus:border-indigo-500"}`}
            />
            {errors.title && (
              <p className="mt-1 text-xs text-red-500">{errors.title}</p>
            )}
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("chat_templates.form.field_category")}{" "}
              <span className="text-gray-400 font-normal">{t("chat_templates.form.field_category_optional")}</span>
            </label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              maxLength={50}
              placeholder={t("chat_templates.form.field_category_placeholder")}
              list="category-suggestions"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-indigo-500 transition-colors"
            />
            {existingCategories.length > 0 && (
              <datalist id="category-suggestions">
                {existingCategories.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            )}
          </div>

          {/* Body */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("chat_templates.form.field_body")} <span className="text-red-500">*</span>
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              placeholder={t("chat_templates.form.field_body_placeholder")}
              className={`w-full px-3 py-2 text-sm border rounded-lg outline-none transition-colors resize-none
                ${errors.body ? "border-red-400 focus:border-red-500" : "border-gray-300 focus:border-indigo-500"}`}
            />
            {errors.body && (
              <p className="mt-1 text-xs text-red-500">{errors.body}</p>
            )}
          </div>

          {/* Attachments */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Attachments <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add file
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                multiple
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            {(existingAttachments.length > 0 || pendingFiles.length > 0) && (
              <ul className="space-y-1">
                {existingAttachments.map((a) => (
                  <li
                    key={a.attachment_id}
                    className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <span className="text-base">{isImage(a) ? "🖼" : "📎"}</span>
                    <span className="flex-1 text-xs text-gray-700 truncate">{a.display_name}</span>
                    <button
                      type="button"
                      onClick={() => openAttachment(a.attachment_id)}
                      className="p-0.5 hover:text-indigo-600 text-gray-400 transition-colors flex-shrink-0"
                      title="Open file"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => removeExisting(a.attachment_id)}
                      className="p-0.5 hover:text-red-500 text-gray-400 transition-colors flex-shrink-0"
                      title="Remove"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </li>
                ))}
                {pendingFiles.map((f, i) => {
                  const key = f.name + f.size;
                  const pct = uploadProgress[key];
                  return (
                    <li
                      key={i}
                      className="flex items-center gap-2 px-2 py-1.5 bg-indigo-50 rounded-lg border border-indigo-200"
                    >
                      <span className="text-base">{f.type.startsWith("image/") ? "🖼" : "📎"}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-indigo-700 truncate">{f.name}</p>
                        {pct !== undefined && (
                          <div className="mt-0.5 h-1 bg-indigo-200 rounded-full overflow-hidden">
                            <div className="h-full bg-indigo-500 transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removePending(i)}
                        disabled={pct !== undefined}
                        className="p-0.5 hover:text-red-500 text-gray-400 transition-colors flex-shrink-0 disabled:opacity-40"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Active toggle */}
          <div className="flex items-center justify-between py-1">
            <div>
              <p className="text-sm font-medium text-gray-700">{t("chat_templates.form.field_active")}</p>
              <p className="text-xs text-gray-400">{t("chat_templates.form.field_active_hint")}</p>
            </div>
            <button
              type="button"
              onClick={() => setIsActive((v) => !v)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors
                ${isActive ? "bg-indigo-600" : "bg-gray-200"}`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform
                  ${isActive ? "translate-x-5" : "translate-x-0"}`}
              />
            </button>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t("chat_templates.form.cancel")}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-60 transition-colors"
            >
              {saving
                ? t("chat_templates.form.saving")
                : template
                  ? t("chat_templates.form.save")
                  : t("chat_templates.form.create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Delete Confirmation ──────────────────────────────────────────────────────

interface DeleteConfirmProps {
  template: ChatTemplate;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

const DeleteConfirmModal = ({ template, onConfirm, onClose }: DeleteConfirmProps) => {
  const { t } = useTranslation();
  const [deleting, setDeleting] = useState(false);

  const handleConfirm = async () => {
    setDeleting(true);
    try {
      await onConfirm();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-2">
          {t("chat_templates.delete.title")}
        </h2>
        <p className="text-sm text-gray-500 mb-6">
          {t("chat_templates.delete.confirm_message", { title: template.title })}
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t("chat_templates.delete.cancel")}
          </button>
          <button
            onClick={handleConfirm}
            disabled={deleting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-60 transition-colors"
          >
            {deleting ? t("chat_templates.delete.deleting") : t("chat_templates.delete.confirm_btn")}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export const ChatTemplatesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<ChatTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // undefined = closed, null = new template, ChatTemplate = editing
  const [formTarget, setFormTarget] = useState<ChatTemplate | null | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<ChatTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Drag-reorder state
  const dragIndex = useRef<number | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await chatTemplateService.getAll();
      setTemplates(data);
    } catch {
      setError(t("chat_templates.load_error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const existingCategories = [
    ...new Set(templates.map((t) => t.category).filter(Boolean) as string[]),
  ];

  const filtered = search.trim()
    ? templates.filter(
        (t) =>
          t.title.toLowerCase().includes(search.toLowerCase()) ||
          t.body.toLowerCase().includes(search.toLowerCase()) ||
          (t.category ?? "").toLowerCase().includes(search.toLowerCase()),
      )
    : templates;

  const handleCreate = async (payload: UpdateTemplatePayload): Promise<ChatTemplate> => {
    const saved = await chatTemplateService.create({ ...payload, sort_order: templates.length });
    return saved;
  };

  const handleUpdate = async (payload: UpdateTemplatePayload): Promise<ChatTemplate> => {
    const saved = await chatTemplateService.update((formTarget as ChatTemplate).id, payload);
    return saved;
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await chatTemplateService.delete(deleteTarget.id);
    setDeleteTarget(null);
    await load();
  };

  // Drag-reorder handlers
  const handleDragStart = (index: number) => { dragIndex.current = index; };
  const handleDrop = async (dropIndex: number) => {
    if (dragIndex.current === null || dragIndex.current === dropIndex) return;
    const reordered = [...templates];
    const [moved] = reordered.splice(dragIndex.current, 1);
    reordered.splice(dropIndex, 0, moved);
    const withOrder = reordered.map((t, i) => ({ ...t, sort_order: i }));
    setTemplates(withOrder);
    dragIndex.current = null;
    try {
      await chatTemplateService.reorder(
        withOrder.map(({ id, sort_order }) => ({ id, sort_order })),
      );
    } catch {
      await load(); // Revert on failure
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-20 bg-gray-50 -mx-3 px-3 md:-mx-6 md:px-6 py-3 mb-4 md:mb-6 border-b border-gray-200">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/other-setup")}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-lg md:text-xl font-bold text-gray-900">{t("chat_templates.title")}</h1>
              <p className="text-xs text-gray-400">{t("chat_templates.subtitle")}</p>
            </div>
          </div>
          <button
            onClick={() => setFormTarget(null)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t("chat_templates.new_template")}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4 relative">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("chat_templates.search_placeholder")}
          className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-indigo-500 transition-colors"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
            {t("chat_templates.loading")}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2 text-gray-400">
            <svg className="w-8 h-8 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            <p className="text-sm">
              {search ? t("chat_templates.empty_search") : t("chat_templates.empty")}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="w-8 px-3 py-3" />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t("chat_templates.col_title")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                  {t("chat_templates.col_category")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t("chat_templates.col_body")}
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t("chat_templates.col_active")}
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t("chat_templates.col_actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((template, index) => (
                <tr
                  key={template.id}
                  draggable={!search}
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop(index)}
                  className="hover:bg-slate-50 transition-colors group"
                >
                  {/* Drag handle */}
                  <td className="px-3 py-3 text-center">
                    <svg
                      className={`w-4 h-4 mx-auto text-gray-300 ${search ? "opacity-0" : "cursor-grab group-hover:text-gray-400"}`}
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M8 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0zm5 0a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0zM8 12a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0zm5 0a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0zM8 18a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0zm5 0a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0z" />
                    </svg>
                  </td>

                  {/* Title */}
                  <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">
                    {template.title}
                  </td>

                  {/* Category */}
                  <td className="px-4 py-3 hidden sm:table-cell">
                    {template.category ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                        {template.category}
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>

                  {/* Body preview */}
                  <td className="px-4 py-3 text-gray-500 max-w-xs">
                    <p className="truncate">
                      {template.body.length > 80
                        ? template.body.slice(0, 80) + "…"
                        : template.body}
                    </p>
                  </td>

                  {/* Active badge */}
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                        ${template.is_active
                          ? "bg-green-50 text-green-700"
                          : "bg-gray-100 text-gray-400"}`}
                    >
                      {template.is_active ? t("chat_templates.active") : t("chat_templates.inactive")}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setFormTarget(template)}
                        className="p-1.5 hover:bg-indigo-50 rounded-lg transition-colors text-gray-400 hover:text-indigo-600"
                        title={t("chat_templates.col_actions")}
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setDeleteTarget(template)}
                        className="p-1.5 hover:bg-red-50 rounded-lg transition-colors text-gray-400 hover:text-red-500"
                        title={t("chat_templates.delete.confirm_btn")}
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Count */}
      {!loading && templates.length > 0 && (
        <p className="mt-3 text-xs text-gray-400 text-right">
          {t("chat_templates.count", { count: templates.length })}
          {search && filtered.length !== templates.length && ` · ${filtered.length} ${t("chat_templates.count_shown")}`}
        </p>
      )}

      {/* Create/Edit Modal */}
      {formTarget !== undefined && (
        <TemplateFormModal
          template={formTarget}
          existingCategories={existingCategories}
          onSave={formTarget === null ? handleCreate : handleUpdate}
          onClose={() => { setFormTarget(undefined); load(); }}
        />
      )}

      {/* Delete Confirm Modal */}
      {deleteTarget && (
        <DeleteConfirmModal
          template={deleteTarget}
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};
